// The lender's loan steps and stage display names. Two uses:
// - Active Deals "By step" (Preview): loans grouped under the lender's loan steps, placed by their pipeline
//   stage. Nothing here decides which stage a loan is in; it only reads `stage_key` and looks it up below.
// - Stage display names, applied to /api/me `stages` as they arrive (lib/queries.ts), so every screen shows
//   them: never "Pre-approved". Display only: the stage keys, `loan_stage` table and its migration are untouched.
import type { Stage } from "./types";
import { STAGE_NOT_SET, STAGE_NOT_SET_LABEL } from "./stageGroups";

export interface LoanStepDef {
  /** The lender's own step number (LendSure: the RM's 11 loan steps; step 1 Intake lives in PQ Triage). */
  n: number;
  key: string;
  name: string;
  /** Short name for the jump strip. */
  short: string;
  /** Pipeline stage keys (`loan_stage.key`) whose loans sit in this step. May be empty. */
  stages: readonly string[];
  /** Shown under an empty step, e.g. when no pipeline stage maps to it. */
  emptyNote?: string;
}

export interface LoanStepConfig {
  lender: string;
  source: string;
  steps: readonly LoanStepDef[];
  /** Display-only stage names for the whole UI, keyed by stage key. The stage table keeps its own label. */
  stageNames: Readonly<Record<string, { label: string; abbr: string }>>;
  /** Pre-Qualification stages preceding `pre_approved` in the UI funnel (`displayStages`). */
  preQualStages?: readonly Stage[];
}

export const LENDSURE_PREQUAL_STAGES: readonly Stage[] = [
  { key: "scenario", label: "Scenario (4-Legs Structuring)", sort: 1, stage_group: "pre", color: "#E9D5FF", text_color: "#14202E" },
  { key: "pq_triage", label: "PQ Triage (Request for PQ)", sort: 2, stage_group: "pre", color: "#BAE6FD", text_color: "#14202E" },
  { key: "pq_analysis", label: "PQ Analysis (Four-Legs Write-Up)", sort: 3, stage_group: "pre", color: "#FDE68A", text_color: "#14202E" },
  { key: "pq_review", label: "PQ Review (Exception / Credit Review)", sort: 4, stage_group: "pre", color: "#FCD34D", text_color: "#14202E" },
];

// ── LENDSURE CONFIGURATION (editable) ────────────────────────────────────────────────────────────────────
// LoanShip is multi-lender; this table is LendSure's, not product behaviour. Edit it here when the RM confirms.
// Source: owner-approved journey map v3, "Stage → step table" (LS2/journey-tree-20261026/tree-v3.md §1 and
// tree-v3.json `stage_to_step`, built 2026-10-26). Step names: v3 / memory loan-steps-vocabulary (2026-10-26).
// Short names: v3 owner_page.role_grid columns.
export const LENDSURE_LOAN_STEPS: LoanStepConfig = {
  lender: "LendSure",
  source: "journey map v3, stage → step table (2026-10-26); RM to confirm (Q1, Q2)",
  steps: [
    { n: 1, key: "pq_intake", name: "Scenario / PQ Triage (Intake)", short: "PQ Triage", stages: ["scenario", "pq_triage"] },
    { n: 2, key: "pq", name: "PQ (CA)", short: "PQ", stages: ["pq_analysis", "pq_review", "pre_approved"] },
    { n: 3, key: "le_preparation", name: "LE preparation", short: "LE prep", stages: ["le_requested"] },
    { n: 4, key: "le_disclosure", name: "LE disclosure", short: "LE disclosure", stages: ["le_sent", "le_signed"] },
    {
      n: 5,
      key: "appraisal",
      name: "Appraisal",
      short: "Appraisal",
      stages: [],
      emptyNote: "No pipeline stage maps to this step yet. Appraisal runs alongside the other steps.",
    },
    { n: 6, key: "final_underwriting", name: "Final underwriting", short: "Final UW", stages: ["submitted_to_uw"] },
    { n: 7, key: "processor_review", name: "Processor review (CLA, with the AM)", short: "With the AM", stages: ["cla_issued"] },
    { n: 8, key: "cd_preparation", name: "CD preparation", short: "CD prep", stages: ["cd_requested", "cd_sent", "cd_signed"] },
    { n: 9, key: "clear_conditions", name: "Clear conditions / CTC / docs", short: "Conditions / CTC", stages: ["ctc_requested", "ctc", "ctc_docs_out"] },
    { n: 10, key: "escrow", name: "Escrow coordination", short: "Escrow", stages: ["ctc_docs_signed"] },
    { n: 11, key: "funded", name: "Funded", short: "Funded", stages: ["funded"] },
  ],
  // LendSure has no "Pre-approved" stage (owner, 2026-10-26): never show that label anywhere in the UI.
  // The Pre-Qual stage path runs Scenario -> PQ Triage -> PQ Analysis -> PQ Review -> Pre-Qual Ready before LE Requested.
  stageNames: {
    scenario: { label: "Scenario (4-Legs Structuring)", abbr: "Scenario" },
    pq_triage: { label: "PQ Triage (Request for PQ)", abbr: "PQ Triage" },
    pq_analysis: { label: "PQ Analysis (Four-Legs Write-Up)", abbr: "PQ Analysis" },
    pq_review: { label: "PQ Review (Exception / Credit Review)", abbr: "PQ Review" },
    pre_approved: { label: "Pre-Qual Ready (PQ Complete)", abbr: "PQ Ready" },
  },
  preQualStages: LENDSURE_PREQUAL_STAGES,
};
// ── end LendSure configuration ───────────────────────────────────────────────────────────────────────────

/** Loans whose stage is known but not in any step of the table (a stage the table has not caught up with). */
export const NO_STEP = "no_step";
export const NO_STEP_LABEL = "Stage not in a step";

export interface StepSection<T> {
  key: string;
  /** null for "Stage not set" and "Stage not in a step". */
  step: LoanStepDef | null;
  label: string;
  items: T[];
}

/**
 * The one stage display mapping: expands Pre-Qual sub-stages before `pre_approved` and applies the lender's
 * display names and sequential sort order. Applied once where /api/me arrives; safe to apply twice.
 */
export function displayStages(stages: readonly Stage[], config: LoanStepConfig = LENDSURE_LOAN_STEPS): Stage[] {
  const existingKeys = new Set(stages.map((s) => s.key));
  const preQualToInsert = (config.preQualStages ?? []).filter((pq) => !existingKeys.has(pq.key));
  const expanded: Stage[] = [];
  for (const s of stages) {
    if (s.key === "pre_approved" && preQualToInsert.length > 0) {
      for (const pq of preQualToInsert) {
        expanded.push({ ...pq });
      }
    }
    expanded.push({ ...s });
  }
  return expanded.map((s, idx) => ({
    ...s,
    sort: idx + 1,
    label: config.stageNames[s.key]?.label ?? s.label,
  }));
}

/** Short chip text for a stage the lender renames; otherwise undefined (use the stage's usual abbreviation). */
export function stageDisplayAbbr(stage: Pick<Stage, "key">, config: LoanStepConfig = LENDSURE_LOAN_STEPS): string | undefined {
  return config.stageNames[stage.key]?.abbr;
}

/** The step a stage sits in, or null when the stage is in no step. */
export function stepOfStage(stageKey: string | null | undefined, config: LoanStepConfig = LENDSURE_LOAN_STEPS): LoanStepDef | null {
  if (!stageKey) return null;
  return config.steps.find((st) => st.stages.includes(stageKey)) ?? null;
}

export const stepLabel = (step: LoanStepDef) => `${step.n}. ${step.name}`;

/**
 * One section per step in step order, every step present even when it has no loans. Loans with no stage, or a
 * stage key the stage list does not have, go to a final "Stage not set" section; a known stage that no step
 * lists goes to "Stage not in a step". Both appear only when they have loans; no loan is dropped. Input order
 * is kept inside each section.
 */
export function groupByStep<T>(items: readonly T[], stages: readonly Stage[], stageOf: (item: T) => string | null | undefined, config: LoanStepConfig = LENDSURE_LOAN_STEPS): StepSection<T>[] {
  const steps = [...config.steps].sort((a, b) => a.n - b.n);
  const sections: StepSection<T>[] = steps.map((step) => ({ key: step.key, step, label: stepLabel(step), items: [] }));
  const byStage = new Map<string, StepSection<T>>();
  for (const sec of sections) for (const k of sec.step!.stages) byStage.set(k, sec);
  const known = new Set([...stages.map((s) => s.key), ...(config.preQualStages ?? []).map((s) => s.key)]);
  const notSet: StepSection<T> = { key: STAGE_NOT_SET, step: null, label: STAGE_NOT_SET_LABEL, items: [] };
  const noStep: StepSection<T> = { key: NO_STEP, step: null, label: NO_STEP_LABEL, items: [] };
  for (const item of items) {
    const key = stageOf(item);
    if (!key || !known.has(key)) notSet.items.push(item);
    else (byStage.get(key) ?? noStep).items.push(item);
  }
  if (noStep.items.length) sections.push(noStep);
  if (notSet.items.length) sections.push(notSet);
  return sections;
}

/** Step sections for everything in view, with each loan placed by its frozen stage (see stageView). */
export function stepView<T extends { id: string; stage_key: string | null }>(
  loans: readonly T[],
  stages: readonly Stage[],
  frozen: ReadonlyMap<string, string | null>,
  config: LoanStepConfig = LENDSURE_LOAN_STEPS,
): { sections: StepSection<T>[]; stale: boolean } {
  const stageOf = (l: T) => (frozen.has(l.id) ? frozen.get(l.id) : l.stage_key);
  return {
    sections: groupByStep(loans, stages, stageOf, config),
    stale: loans.some((l) => frozen.has(l.id) && frozen.get(l.id) !== l.stage_key),
  };
}

export type StepRow<T> = { type: "group"; key: string; label: string; summary: string; collapsed: boolean } | { type: "loan"; loan: T };

/**
 * Grid rows for the step sections: one header per section (empty ones say "None"), then its loans unless
 * collapsed. The grid's label cell is one column wide, so it carries the number and short name; a longer full
 * name leads the summary, which spans the row.
 */
export function stepRows<T>(sections: readonly StepSection<T>[], collapsed: ReadonlySet<string>, summarize: (items: T[]) => string): StepRow<T>[] {
  const out: StepRow<T>[] = [];
  for (const sec of sections) {
    const key = `step:${sec.key}`;
    const isCollapsed = collapsed.has(key);
    const counts = sec.items.length ? summarize(sec.items) : "None";
    const label = sec.step ? `${sec.step.n}. ${sec.step.short}` : sec.label;
    const summary = sec.step && sec.step.name !== sec.step.short ? `${sec.step.name}  ·  ${counts}` : counts;
    out.push({ type: "group", key, label: label.toUpperCase(), summary, collapsed: isCollapsed });
    if (!isCollapsed) for (const loan of sec.items) out.push({ type: "loan", loan });
  }
  return out;
}

export type StepMark = "past" | "current" | "alongside";

/**
 * The loan's steps reached so far, from its stage alone: the steps before its current step and the current one.
 * A step no stage maps to (appraisal) is "alongside": the stage says nothing about it. No completion is
 * claimed; "past" only means the loan's stage is beyond that step. Empty when the stage is in no step.
 */
export function stepsReached(stageKey: string | null | undefined, config: LoanStepConfig = LENDSURE_LOAN_STEPS): { step: LoanStepDef; mark: StepMark }[] {
  const cur = stepOfStage(stageKey, config);
  if (!cur) return [];
  return [...config.steps]
    .sort((a, b) => a.n - b.n)
    .filter((st) => st.n <= cur.n)
    .map((step) => ({ step, mark: step === cur ? "current" : step.stages.length === 0 ? "alongside" : "past" }));
}
